import { getServerUrl, publicAnonKey, supabase } from './woocommerce';

const KV_KEY = 'global_ads_config_v1';

export type AdSlot =
  | 'landing_top'
  | 'landing_bottom'
  | 'dashboard_top'
  | 'orders_top'
  | 'products_top';

export interface AdsConfig {
  enabled: boolean;
  zones: Partial<Record<AdSlot, string>>;
}

export const AD_SLOTS: { key: AdSlot; label: string }[] = [
  { key: 'landing_top', label: 'Landing — após o Hero' },
  { key: 'landing_bottom', label: 'Landing — antes do CTA final' },
  { key: 'dashboard_top', label: 'Dashboard — após cartões de resumo' },
  { key: 'orders_top', label: 'Encomendas — topo' },
  { key: 'products_top', label: 'Produtos — topo' },
];

const authHeaders = () => ({
  'Content-Type': 'application/json',
  apikey: publicAnonKey,
  Authorization: `Bearer ${publicAnonKey}`,
});

let cache: { config: AdsConfig | null; at: number } | null = null;

export async function loadAdsConfig(force = false): Promise<AdsConfig | null> {
  if (cache && !force && Date.now() - cache.at < 60_000) return cache.config;
  try {
    const res = await fetch(getServerUrl(`kv/${KV_KEY}`), { headers: authHeaders() });
    const json = await res.json().catch(() => null);
    const data = json?.data;
    const config: AdsConfig | null =
      data && typeof data === 'object'
        ? { enabled: data.enabled !== false, zones: data.zones || {} }
        : null;
    cache = { config, at: Date.now() };
    return config;
  } catch {
    return cache?.config ?? null;
  }
}

export async function saveAdsConfig(config: AdsConfig): Promise<boolean> {
  try {
    const res = await fetch(getServerUrl(`kv/${KV_KEY}`), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ data: config }),
    });
    if (!res.ok) return false;
    cache = { config, at: Date.now() };
    return true;
  } catch {
    return false;
  }
}

// A store is on the free plan while it has no subscription_plan_id on its
// white-label row — the free onboarding tier never links a paid plan.
const planCache: Record<string, boolean> = {};

function activeStoreSlug(): string {
  try {
    const activeId = localStorage.getItem('wc_active_store_id');
    const ms = JSON.parse(localStorage.getItem('wc_multistore') || '[]');
    const store = ms.find((x: any) => x.id === activeId);
    return store?.config?.customUrl || localStorage.getItem('pwa_store_slug') || '';
  } catch {
    return localStorage.getItem('pwa_store_slug') || '';
  }
}

export async function isActiveStoreFreePlan(): Promise<boolean> {
  const slug = activeStoreSlug().toLowerCase();
  if (!slug || slug === 'default') return false; // master context shows no ads
  if (slug in planCache) return planCache[slug];
  try {
    const { data } = await supabase
      .from('white_labels')
      .select('subscription_plan_id')
      .eq('slug', slug)
      .maybeSingle();
    // Free tier = no linked plan, or a €0 plan (e.g. Starter). Paid plans
    // (Básico/Premium) suppress ads.
    let free = !data?.subscription_plan_id;
    if (!free) {
      const { data: plan } = await supabase
        .from('subscription_plans')
        .select('monthly_price')
        .eq('id', data.subscription_plan_id)
        .maybeSingle();
      free = !plan || Number(plan.monthly_price || 0) <= 0;
    }
    planCache[slug] = free;
    return free;
  } catch {
    return false;
  }
}
